import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useLocale } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';

import graph1 from './images/graph1.webp';
import graph1en from './images/graph1en.webp';
import graph2 from './images/graph2.webp';
import graph2en from './images/graph2en.webp';
import graph3 from './images/graph3.webp';
import graph3en from './images/graph3en.webp';
import graph4 from './images/graph4.webp';
import graph4en from './images/graph4en.webp';

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export const OrderConversionListGatewayReferenceDialog: React.FC<Props> = ({ open, onClose }) => {
  const locale = useLocale();
  const translate = useTypedTranslate();
  const isEnglish = locale === 'en';

  return (
    <Dialog onClose={onClose} open={open}>
      <Box sx={{ width: 600 }}>
        <CardHeader
          appendActions={<CloseIconButton key="close-button" onClose={onClose} />}
          sx={{ height: 'auto' }}
          title={translate('app.orderConversion.setupGuide')}
          titleSize="smallBold"
        />
        <CardContent
          sx={{ paddingTop: '0!important', overflow: 'auto', maxHeight: 'calc(100vh - 160px)' }}
        >
          <Typography fontSize={16} fontWeight={500} sx={{ marginBottom: '8px' }} variant="body1">
            {translate('app.orderConversion.referenceDialog.monitoringTitle')}
          </Typography>
          <Typography variant="body2">
            {translate('app.orderConversion.referenceDialog.monitoringDescription')}
          </Typography>
          <img
            alt="graph1"
            src={isEnglish ? graph1en : graph1}
            style={{ margin: '16px 0', width: '100%' }}
          />
          <Typography variant="body2">
            {translate('app.orderConversion.referenceDialog.monitoringValues')}
          </Typography>
          <Typography fontSize={16} fontWeight={500} sx={{ margin: '16px 0' }} variant="body1">
            {translate('app.orderConversion.referenceDialog.thresholdsTitle')}
          </Typography>
          <Typography variant="body2">
            <b style={{ fontWeight: 500 }}>
              {translate('app.orderConversion.referenceDialog.successfulOrdersDeviation')}
            </b>{' '}
            {translate('app.orderConversion.referenceDialog.successfulOrdersDesc')}
          </Typography>
          <img
            alt="graph2"
            src={isEnglish ? graph2en : graph2}
            style={{ margin: '16px 0', width: '100%' }}
          />
          <ul style={{ marginTop: 0, paddingLeft: '16px' }}>
            <li>
              <Typography variant="body2">
                <b style={{ color: '#2E7D32', fontWeight: 500 }}>
                  {translate('app.orderConversion.referenceDialog.positiveStatus')}
                </b>{' '}
                - {translate('app.orderConversion.referenceDialog.positiveDesc')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <b style={{ color: '#ED6C02', fontWeight: 500 }}>
                  {translate('app.orderConversion.referenceDialog.acceptableStatus')}
                </b>{' '}
                - {translate('app.orderConversion.referenceDialog.acceptableDesc')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <b style={{ color: '#D32F2F', fontWeight: 500 }}>
                  {translate('app.orderConversion.referenceDialog.deviationStatus')}
                </b>{' '}
                - {translate('app.orderConversion.referenceDialog.deviationDesc')}
              </Typography>
            </li>
          </ul>
          <Typography variant="body2">
            <b>{translate('app.orderConversion.referenceDialog.cancelledOrdersDeviation')}</b>{' '}
            {translate('app.orderConversion.referenceDialog.cancelledOrdersDesc')}
          </Typography>
          <img
            alt="graph3"
            src={isEnglish ? graph3en : graph3}
            style={{ margin: '16px 0', width: '100%' }}
          />
          <Typography variant="body2">
            <b style={{ fontWeight: 500 }}>
              {translate('app.orderConversion.referenceDialog.queuedOrdersDeviation')}
            </b>{' '}
            {translate('app.orderConversion.referenceDialog.queuedOrdersDesc')}
          </Typography>
          <img
            alt="graph4"
            src={isEnglish ? graph4en : graph4}
            style={{ margin: '16px 0', width: '100%' }}
          />
          <Typography variant="body2">
            <b style={{ fontWeight: 500 }}>
              {translate('app.orderConversion.referenceDialog.importantNote')}
            </b>{' '}
            {translate('app.orderConversion.referenceDialog.thresholdsSensitivity')}
          </Typography>
          <CardActionsButtons
            rightActionsSlot={
              <Button label={translate('app.buttons.close')} onClick={onClose} variant="outlined" />
            }
            sx={{ paddingRight: 0, paddingBottom: 0 }}
          />
        </CardContent>
      </Box>
    </Dialog>
  );
};
